
import { useState } from 'react';
import { useDarkMode } from "../../../../context/ThemeContext";

export default function PoliciesSection() {
  const { isDarkMode } = useDarkMode();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);

  const themeClasses = {
    bg: isDarkMode ? 'bg-dark-surface' : 'bg-white',
    border: isDarkMode ? 'border-dark-border' : 'border-gray-200',
    text: isDarkMode ? 'text-white' : 'text-gray-900',
    textSecondary: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    cardBg: isDarkMode ? 'bg-dark-card' : 'bg-white',
    cardBorder: isDarkMode ? 'border-dark-border' : 'border-gray-200',
    buttonHover: isDarkMode ? 'hover:bg-dark-card' : 'hover:bg-gray-100',
    buttonText: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    buttonTextHover: isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'
  };

  const policies = [
    {
      id: 'POL-001',
      name: 'Seguro Integral Flota',
      type: 'comprehensive',
      provider: 'AXA Seguros',
      policyNumber: 'AXA-FL-2024-001',
      status: 'active',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      premium: '$125,000',
      coverage: '$2,500,000',
      vehicles: 15,
      deductible: '$10,000',
      lastClaim: '2024-02-15',
      claimsCount: 2
    },
    {
      id: 'POL-002',
      name: 'Responsabilidad Civil',
      type: 'liability',
      provider: 'MAPFRE',
      policyNumber: 'MAP-RC-2024-002',
      status: 'active',
      startDate: '2024-01-15',
      endDate: '2024-12-31',
      premium: '$45,000',
      coverage: '$1,000,000',
      vehicles: 24,
      deductible: '$5,000',
      lastClaim: 'Sin reclamaciones',
      claimsCount: 0
    },
    {
      id: 'POL-003',
      name: 'Seguro de Carga',
      type: 'cargo',
      provider: 'Zurich Seguros',
      policyNumber: 'ZUR-CG-2024-003',
      status: 'active',
      startDate: '2024-02-01',
      endDate: '2025-01-31',
      premium: '$78,000',
      coverage: '$5,000,000',
      vehicles: 18,
      deductible: '$15,000',
      lastClaim: '2024-03-10',
      claimsCount: 1
    },
    {
      id: 'POL-004',
      name: 'Seguro Básico Remolques',
      type: 'basic',
      provider: 'GNP Seguros',
      policyNumber: 'GNP-BS-2024-004',
      status: 'expiring',
      startDate: '2023-12-01',
      endDate: '2024-11-30',
      premium: '$32,000',
      coverage: '$800,000',
      vehicles: 8,
      deductible: '$8,000',
      lastClaim: '2024-01-20',
      claimsCount: 3
    },
    {
      id: 'POL-005',
      name: 'Cobertura Especial GPS',
      type: 'equipment',
      provider: 'HDI Seguros',
      policyNumber: 'HDI-EQ-2024-005',
      status: 'inactive',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      premium: '$18,000',
      coverage: '$200,000',
      vehicles: 28,
      deductible: '$2,000',
      lastClaim: 'Sin reclamaciones',
      claimsCount: 0
    }
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'comprehensive': return 'text-blue-500 bg-blue-500/10';
      case 'liability': return 'text-primary-500 bg-primary-500/10';
      case 'cargo': return 'text-purple-500 bg-purple-500/10';
      case 'basic': return 'text-amber-500 bg-amber-500/10';
      case 'equipment': return 'text-cyan-500 bg-cyan-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'comprehensive': return 'Integral';
      case 'liability': return 'Responsabilidad';
      case 'cargo': return 'Carga';
      case 'basic': return 'Básico';
      case 'equipment': return 'Equipos';
      default: return 'Otro';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-primary-500 bg-primary-500/10';
      case 'inactive': return 'text-red-500 bg-red-500/10';
      case 'expiring': return 'text-amber-500 bg-amber-500/10';
      case 'expired': return 'text-gray-500 bg-gray-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Activa';
      case 'inactive': return 'Inactiva';
      case 'expiring': return 'Por vencer';
      case 'expired': return 'Vencida';
      default: return 'Desconocido';
    }
  };

  // const formatCurrency = (amount: string) => {
  //   return amount.replace('$', '').replace(',', '') + ' MXN';
  // };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`${themeClasses.bg} border-b ${themeClasses.border} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className={`text-2xl font-bold ${themeClasses.text}`}>Gestión de Pólizas</h1>
            <p className={`${themeClasses.textSecondary} mt-1`}>Administra todas las pólizas de seguros de la flota</p>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-primary-500 hover:bg-primary-400 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            <span>Nueva Póliza</span>
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className={`px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 pr-8`}
          >
            <option value="all">Todas las pólizas</option>
            <option value="active">Activas</option>
            <option value="expiring">Por vencer</option>
            <option value="inactive">Inactivas</option>
            <option value="expired">Vencidas</option>
          </select>

          <select
            className={`px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 pr-8`}
          >
            <option value="all">Todos los tipos</option>
            <option value="comprehensive">Integral</option>
            <option value="liability">Responsabilidad</option>
            <option value="cargo">Carga</option>
            <option value="basic">Básico</option>
            <option value="equipment">Equipos</option>
          </select>

          <div className="relative">
            <i className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}></i>
            <input
              type="text"
              placeholder="Buscar por número de póliza..."
              className={`pl-10 pr-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-64`}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {policies.map((policy) => (
            <div key={policy.id} className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-6 hover:shadow-lg transition-all`}>
              {/* Policy Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${themeClasses.text} text-lg`}>{policy.name}</h3>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>{policy.provider} • {policy.id}</p>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(policy.status)}`}>
                    {getStatusText(policy.status)}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(policy.type)}`}>
                    {getTypeText(policy.type)}
                  </span>
                </div>
              </div>

              {/* Policy Number */}
              <div className={`p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg mb-4`}>
                <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Número de póliza</p>
                <p className={`text-sm ${themeClasses.text} font-mono font-semibold`}>{policy.policyNumber}</p>
              </div>

              {/* Policy Details */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Vigencia</p>
                  <p className={`text-sm ${themeClasses.text}`}>{policy.startDate}</p>
                  <p className={`text-sm ${themeClasses.text}`}>a {policy.endDate}</p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Vehículos cubiertos</p>
                  <p className={`text-sm ${themeClasses.text} font-semibold`}>{policy.vehicles} unidades</p>
                </div>
              </div>

              {/* Financial Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Prima anual</p>
                  <p className={`text-sm ${themeClasses.text} font-semibold text-primary-500`}>{policy.premium}</p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Cobertura</p>
                  <p className={`text-sm ${themeClasses.text} font-semibold`}>{policy.coverage}</p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Deducible</p>
                  <p className={`text-sm ${themeClasses.text}`}>{policy.deductible}</p>
                </div>
                <div>
                  <p className={`text-xs ${themeClasses.textSecondary} mb-1`}>Reclamaciones</p>
                  <p className={`text-sm ${policy.claimsCount > 0 ? 'text-amber-500' : 'text-primary-500'} font-semibold`}>
                    {policy.claimsCount}
                  </p>
                </div>
              </div>

              {/* Last Claim */}
              <div className={`p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg mb-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <i className={`ri-file-damage-line text-sm ${themeClasses.textSecondary}`}></i>
                    <span className={`text-sm ${themeClasses.textSecondary}`}>Última reclamación</span>
                  </div>
                  <span className={`text-sm ${themeClasses.text}`}>{policy.lastClaim}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button className={`flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors`}>
                  Editar
                </button>
                <button className="py-2 px-3 bg-primary-500 hover:bg-primary-400 text-white rounded-lg text-sm font-medium transition-colors">
                  Ver Detalles
                </button>
                <button aria-label="Descargar" className={`py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm transition-colors`}>
                  <i className="ri-download-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Policy Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.cardBg} rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${themeClasses.text}`}>Nueva Póliza de Seguro</h3>
              <button 
                onClick={() => setShowAddModal(false)}
                className={`${themeClasses.buttonText} ${themeClasses.buttonTextHover} p-1`}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Nombre de la póliza</label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="Ej: Seguro Integral Flota 2024"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Tipo de póliza</label>
                  <select className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500 pr-8`}>
                    <option value="">Seleccionar tipo</option>
                    <option value="comprehensive">Integral</option>
                    <option value="liability">Responsabilidad Civil</option>
                    <option value="cargo">Seguro de Carga</option>
                    <option value="basic">Básico</option>
                    <option value="equipment">Equipos</option>
                  </select>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Aseguradora</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="Nombre de la aseguradora"
                  />
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Número de póliza</label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="Número único de la póliza"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Fecha de inicio</label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Fecha de vencimiento</label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Prima anual</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="$125,000"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Cobertura</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="$2,500,000"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Deducible</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder="$10,000"
                  />
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>Vehículos cubiertos</label>
                <input
                  type="number"
                  className={`w-full px-3 py-2 ${isDarkMode ? 'bg-dark-card' : 'bg-gray-50'} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="Número de vehículos"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className={`flex-1 py-2 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 px-4 bg-primary-500 hover:bg-primary-400 text-white rounded-lg font-medium transition-colors"
                >
                  Guardar Póliza
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
